import api from "../core/axios";
import APIResponse from "../types/response.type";

// ============================================================================
// Enums
// ============================================================================

export type EmailDomainStatus =
  | "PENDING"
  | "VERIFYING"
  | "ACTIVE"
  | "DEGRADED"
  | "SUSPENDED";

export type EmailSenderStatus = "PENDING" | "ACTIVE" | "SUSPENDED";

export type DnsRecordType = "TXT" | "CNAME" | "MX";

// ============================================================================
// Domain Types
// ============================================================================

export interface DomainDnsRecord {
  id: string;
  emailDomainId: string;
  recordType: DnsRecordType;
  host: string;
  expectedValue: string;
  verified: boolean;
  lastCheckedAt: string | null;
}

export interface EmailDomain {
  id: string;
  organizationId: string;
  domainName: string;
  status: EmailDomainStatus;
  dkimKeyId: string | null;
  sesIdentityArn: string | null;
  verificationToken: string;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  dnsRecords: DomainDnsRecord[];
}

export interface DnsStatusRecord {
  id: string;
  recordType: DnsRecordType;
  host: string;
  expectedValue: string;
  verified: boolean;
  resolvedValue: string | null;
  checkedAt: string;
  error: string | null;
}

export interface DnsStatusResponse {
  domainId: string;
  domainName: string;
  status: EmailDomainStatus;
  records: DnsStatusRecord[];
}

export interface DomainHealthResponse {
  domainId: string;
  domainName: string;
  status: EmailDomainStatus;
  verifiedRecords: number;
  totalRecords: number;
  lastPolledAt: string | null;
  activeSenderCount: number;
  failingRecords: DnsRecordType[];
}

export interface RegisterDomainDto {
  domainName: string;
}

// ============================================================================
// Sender Types
// ============================================================================

export interface EmailSender {
  id: string;
  emailDomainId: string;
  organizationId: string;
  emailAddress: string;
  displayName: string | null;
  replyTo: string | null;
  status: EmailSenderStatus;
  verificationToken: string | null;
  verificationExpiresAt: string | null;
  dailyQuota: number;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterSenderDto {
  emailAddress: string;
  displayName?: string;
  replyTo?: string;
  emailDomainId: string;
}

export interface UpdateSenderDto {
  displayName?: string;
  replyTo?: string;
}

// ============================================================================
// Message Types
// ============================================================================

export type EmailDeliveryStatus =
  | "QUEUED"
  | "SENT"
  | "DELIVERED"
  | "BOUNCED"
  | "COMPLAINED"
  | "OPENED"
  | "CLICKED";

export type DeliveryEventType =
  | "queued"
  | "sent"
  | "delivered"
  | "bounced"
  | "complained"
  | "opened"
  | "clicked";

export interface EmailAttachmentMeta {
  filename: string;
  contentType: string;
  size: number;
  /** Present when the attachment is stored in the file library */
  fileId?: string;
  sizeBytes?: number;
  s3Key?: string | null;
  url?: string;
}

export interface EmailMessage {
  id: string;
  organizationId: string;
  conversationId: string | null;
  senderId: string;
  senderAddress: string;
  senderDisplayName: string | null;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: EmailAttachmentMeta[];
  deliveryStatus: EmailDeliveryStatus;
  sesMessageId: string | null;
  messageIdHeader: string | null;
  headers: Record<string, string> | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryEvent {
  id: string;
  messageId: string;
  type: DeliveryEventType;
  timestamp: string;
  bounceType?: string | null;
  bounceSubType?: string | null;
  bounceReason?: string | null;
  complaintFeedbackType?: string | null;
  rawPayload?: Record<string, unknown> | null;
  createdAt: string;
}

export interface SendEmailPayload {
  from: string;
  to: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  conversationId?: string;
  /** File library UUIDs to attach (preferred over base64 inline attachments) */
  fileIds?: string[];
  attachments?: {
    filename: string;
    contentType: string;
    content: string; // base64
  }[];
}

export interface SendEmailResponse {
  messageId: string;
  conversationId: string;
  status: "queued";
}

export interface SenderQuotaUsage {
  senderId: string;
  dailySent: number;
  dailyQuota: number;
  quotaResetsAt: string;
  domainDailySent: number;
  domainDailyQuota: number;
}

// ============================================================================
// Conversation Types (Inbound / Inbox)
// ============================================================================

export type ConversationStatus = "open" | "closed" | "pending";

export interface EmailConversation {
  id: string;
  status: ConversationStatus;
  emailSubject: string | null;
  emailParticipants: string[] | null;
  unreadCount: number;
  lastMessageAt: string | null;
  emailMessageCount: number;
  createdAt: string;
  updatedAt: string;
  lastMessage: {
    id: string;
    fromAddress: string;
    bodyText: string | null;
    createdAt: string;
    direction: "INBOUND" | "OUTBOUND";
  } | null;
}

export interface ConversationMessage {
  id: string;
  direction: "INBOUND" | "OUTBOUND";
  fromAddress: string;
  toAddresses: string[];
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: EmailAttachmentMeta[] | null;
  messageIdHeader: string | null;
  inReplyTo: string | null;
  deliveryStatus: EmailDeliveryStatus;
  createdAt: string;
}

export interface UnreadSummary {
  totalUnread: number;
  conversationsWithUnread: number;
}

export interface ConversationFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: ConversationStatus;
  sortBy?: "lastMessageAt" | "createdAt" | "unreadCount";
  sortOrder?: "asc" | "desc";
}

export interface MessageFilters {
  cursor?: string;
  limit?: number;
  status?: EmailDeliveryStatus;
  senderId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CursorPaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
  prevCursor: string | null;
  total: number;
}

// ============================================================================
// Paginated Response
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// Email Service — Domain Methods
// ============================================================================

class EmailService {
  // ── Domain Endpoints ──────────────────────────────────────────────────

  async getDomains(
    params?: { page?: number; limit?: number },
  ): Promise<APIResponse<PaginatedResponse<EmailDomain>>> {
    const response = await api.get("/email/domains", { params });
    return response.data;
  }

  async registerDomain(
    dto: RegisterDomainDto,
  ): Promise<APIResponse<EmailDomain>> {
    const response = await api.post("/email/domains", dto);
    return response.data;
  }

  async getDomain(id: string): Promise<APIResponse<EmailDomain>> {
    const response = await api.get(`/email/domains/${id}`);
    return response.data;
  }

  async getDnsStatus(id: string): Promise<APIResponse<DnsStatusResponse>> {
    const response = await api.get(`/email/domains/${id}/dns-status`);
    return response.data;
  }

  async verifyDomain(id: string): Promise<APIResponse<EmailDomain>> {
    const response = await api.post(`/email/domains/${id}/verify`);
    return response.data;
  }

  async deleteDomain(id: string): Promise<void> {
    await api.delete(`/email/domains/${id}`);
  }

  async getDomainHealth(
    id: string,
  ): Promise<APIResponse<DomainHealthResponse>> {
    const response = await api.get(`/email/domains/${id}/health`);
    return response.data;
  }

  // ── Sender Endpoints ──────────────────────────────────────────────────

  async getSenders(
    params?: { page?: number; limit?: number; domainId?: string; status?: EmailSenderStatus },
  ): Promise<APIResponse<PaginatedResponse<EmailSender>>> {
    const response = await api.get("/email/senders", { params });
    return response.data;
  }

  async registerSender(
    dto: RegisterSenderDto,
  ): Promise<APIResponse<EmailSender>> {
    const response = await api.post("/email/senders", dto);
    return response.data;
  }

  async confirmSenderVerification(
    senderId: string,
    token: string,
  ): Promise<APIResponse<EmailSender>> {
    const response = await api.get(
      `/email/senders/${senderId}/verify/${token}`,
    );
    return response.data;
  }

  async resendSenderVerification(
    senderId: string,
  ): Promise<APIResponse<EmailSender>> {
    const response = await api.post(`/email/senders/${senderId}/verify`);
    return response.data;
  }

  async updateSender(
    senderId: string,
    dto: UpdateSenderDto,
  ): Promise<APIResponse<EmailSender>> {
    const response = await api.patch(`/email/senders/${senderId}`, dto);
    return response.data;
  }

  async deleteSender(senderId: string): Promise<void> {
    await api.delete(`/email/senders/${senderId}`);
  }

  // ── Message Endpoints ─────────────────────────────────────────────────

  async sendMessage(
    payload: SendEmailPayload,
  ): Promise<APIResponse<SendEmailResponse>> {
    const response = await api.post("/email/messages/send", payload);
    return response.data;
  }

  async getMessages(
    params?: MessageFilters,
  ): Promise<APIResponse<CursorPaginatedResponse<EmailMessage>>> {
    const response = await api.get("/email/messages", { params });
    return response.data;
  }

  async getMessage(id: string): Promise<APIResponse<EmailMessage>> {
    const response = await api.get(`/email/messages/${id}`);
    return response.data;
  }

  async getDeliveryEvents(
    messageId: string,
  ): Promise<APIResponse<DeliveryEvent[]>> {
    const response = await api.get(`/email/messages/${messageId}/events`);
    return response.data;
  }

  async getSenderQuota(
    senderId: string,
  ): Promise<APIResponse<SenderQuotaUsage>> {
    const response = await api.get(`/email/messages/senders/${senderId}/quota`);
    return response.data;
  }

  /**
   * Get a short-lived (5-min) presigned S3 URL for an email attachment.
   * The backend redirects — axios follows the redirect and returns the final URL
   * via response.request.responseURL, so we return the response config URL.
   */
  async getAttachmentDownloadUrl(
    messageId: string,
    index: number,
  ): Promise<string> {
    const response = await api.get(
      `/email/messages/${messageId}/attachments/${index}/download`,
      { maxRedirects: 5, responseType: 'blob' },
    );
    // The presigned URL is the final URL after redirect
    return (response.request as XMLHttpRequest).responseURL || response.config.url!;
  }

  // ── Conversation / Inbox Endpoints ────────────────────────────────────

  async getConversations(
    params?: ConversationFilters,
  ): Promise<APIResponse<PaginatedResponse<EmailConversation>>> {
    const response = await api.get("/email/conversations", { params });
    return response.data;
  }

  async getConversation(
    id: string,
  ): Promise<APIResponse<EmailConversation>> {
    const response = await api.get(`/email/conversations/${id}`);
    return response.data;
  }

  async getConversationMessages(
    conversationId: string,
    params?: { page?: number; limit?: number },
  ): Promise<APIResponse<PaginatedResponse<ConversationMessage>>> {
    const response = await api.get(
      `/email/conversations/${conversationId}/messages`,
      { params },
    );
    return response.data;
  }

  async markConversationRead(conversationId: string): Promise<APIResponse<{ success: boolean }>> {
    const response = await api.patch(
      `/email/conversations/${conversationId}/read`,
    );
    return response.data;
  }

  async updateConversationStatus(
    conversationId: string,
    status: ConversationStatus,
  ): Promise<APIResponse<EmailConversation>> {
    const response = await api.patch(
      `/email/conversations/${conversationId}/status/${status}`,
    );
    return response.data;
  }

  async getUnreadSummary(): Promise<APIResponse<UnreadSummary>> {
    const response = await api.get("/email/conversations/unread");
    return response.data;
  }
}

export default new EmailService();
